import React, { useState } from "react";
import { Button } from "./button";
import { cn } from "../../utils/utils";

// Simple Modal/Dialog component
const AlertDialog = ({ children }) => {
  const [open, setOpen] = useState(false);
  
  return (
    <AlertDialogContext.Provider value={{ open, setOpen }}>
      {children}
    </AlertDialogContext.Provider>
  );
};

const AlertDialogContext = React.createContext();

const AlertDialogTrigger = ({ children, asChild, ...props }) => {
  const { setOpen } = React.useContext(AlertDialogContext);
  
  if (asChild) {
    return React.cloneElement(children, {
      ...props,
      onClick: (e) => {
        setOpen(true);
        children.props.onClick?.(e);
      }
    });
  }
  
  return (
    <button {...props} onClick={() => setOpen(true)}>
      {children}
    </button>
  );
};

const AlertDialogContent = ({ children, className, ...props }) => {
  const { open, setOpen } = React.useContext(AlertDialogContext);
  
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50" 
        onClick={() => setOpen(false)}
      />
      
      {/* Dialog */}
      <div 
        className={cn(
          "relative bg-white rounded-lg shadow-lg max-w-md w-full mx-4 p-6",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </div>
  );
};

const AlertDialogHeader = ({ children, className, ...props }) => (
  <div className={cn("flex flex-col space-y-2 text-center sm:text-left mb-4", className)} {...props}>
    {children}
  </div>
);

const AlertDialogTitle = ({ children, className, ...props }) => (
  <h3 className={cn("text-lg font-semibold", className)} {...props}>
    {children}
  </h3>
);

const AlertDialogDescription = ({ children, className, ...props }) => (
  <p className={cn("text-sm text-gray-500", className)} {...props}>
    {children}
  </p>
);

const AlertDialogFooter = ({ children, className, ...props }) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-4", className)} {...props}>
    {children}
  </div>
);

const AlertDialogCancel = ({ children, ...props }) => {
  const { setOpen } = React.useContext(AlertDialogContext);
  
  return (
    <Button
      variant="outline"
      onClick={() => setOpen(false)}
      {...props}
    >
      {children}
    </Button>
  );
};

const AlertDialogAction = ({ children, onClick, ...props }) => {
  const { setOpen } = React.useContext(AlertDialogContext);
  
  const handleClick = (e) => {
    onClick?.(e);
    setOpen(false);
  };
  
  return (
    <Button onClick={handleClick} {...props}>
      {children}
    </Button>
  );
};

export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
};